import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCountry } from "@/lib/CountryContext";
import { fetchStatsByCountry } from "@/lib/api";
import { COUNTRIES, ACTIVE_COUNTRIES } from "@/lib/countries";

/**
 * Horizontal pill bar showing live annonce counts per UEMOA country.
 * Clicking a pill switches the active country and navigates to /annonces.
 */
export default function CountryAnnonceCounter() {
  const navigate = useNavigate();
  const { code: activeCode, setCountry } = useCountry();
  const [counts, setCounts] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchStatsByCountry()
      .then((d) => { if (!cancelled) setCounts(d.counts || {}); })
      .catch(() => { if (!cancelled) setCounts({}); });
    return () => { cancelled = true; };
  }, []);

  if (!counts) return null;

  const handleClick = (code) => {
    setCountry(code);
    navigate("/annonces");
  };

  // Sort by count desc so the most active markets come first; CI keeps its spot
  // first when tied (visual continuity).
  const sorted = [...ACTIVE_COUNTRIES].sort((a, b) => {
    if (a === "CI" && (counts[a] || 0) >= (counts[b] || 0)) return -1;
    if (b === "CI" && (counts[b] || 0) >= (counts[a] || 0)) return 1;
    return (counts[b] || 0) - (counts[a] || 0);
  });

  return (
    <section
      className="max-w-7xl mx-auto px-6 py-6"
      data-testid="country-counter-section"
      aria-label="Nombre d'annonces actives par pays"
    >
      <div className="glass-card p-4 md:p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h2 className="text-sm font-bold text-[var(--ink-soft)] uppercase tracking-widest">
            Marché UEMOA en direct
          </h2>
          <span className="text-[11px] text-[var(--ink-soft)]">
            Cliquez sur un pays pour voir ses annonces
          </span>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="country-counter-list">
          {sorted.map((code) => {
            const c = COUNTRIES[code];
            const n = counts[code] || 0;
            const isActive = code === activeCode;
            return (
              <button
                key={code}
                type="button"
                onClick={() => handleClick(code)}
                data-testid={`country-counter-${code}`}
                className={
                  "inline-flex items-center gap-2 px-3 py-2 rounded-full border-2 text-sm transition-all hover:translate-y-[-1px] " +
                  (isActive
                    ? "border-[var(--brand)] bg-[var(--country-bg)] font-bold shadow-md"
                    : "border-[var(--line)] bg-white/60 hover:border-[var(--brand)] hover:bg-white")
                }
                title={`${c.name} — ${n} annonce${n > 1 ? "s" : ""}`}
              >
                <span className="text-base leading-none">{c.flag}</span>
                <span className="font-semibold text-[var(--ink)]">{c.name}</span>
                <span
                  className={
                    "min-w-[28px] text-center px-2 py-0.5 rounded-full text-xs font-bold " +
                    (n > 0
                      ? "bg-[var(--brand-soft)] text-[var(--brand)]"
                      : "bg-gray-100 text-gray-500")
                  }
                >
                  {n.toLocaleString("fr-FR")}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
